import { useState, useEffect, useCallback } from 'react';
import { Play, Clock, Globe, Info, Megaphone } from 'lucide-react';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import apiClient, { getPublicBaseUrl } from '@/lib/api';
import { ConfigFieldWithMode, FieldOption, AvailableVariable } from './ConfigFieldWithMode';

interface TriggerConfigProps {
  config: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  availableVariables?: AvailableVariable[];
}

async function fetchOptions(endpoint: string): Promise<FieldOption[]> {
  try {
    const response = await apiClient.get<{ options?: FieldOption[] }>(`/workflows/options/${endpoint}`);
    // Handle both direct response and wrapped response formats.
    // The wire body is either { options } or { data: { options } }; apiClient
    // returns the raw JSON typed as ApiResponse, so read both shapes defensively.
    const body = response as { options?: FieldOption[]; data?: { options?: FieldOption[] } };
    return body.options || body.data?.options || [];
  } catch (error) {
    console.error(`Failed to fetch ${endpoint}:`, error);
    return [];
  }
}

export function ManualTriggerConfig({ config, onChange }: TriggerConfigProps) {
  return (
    <div className="space-y-4">
      <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex items-center gap-2 mb-2">
          <Play className="h-4 w-4 text-green-600 dark:text-green-400" />
          <p className="font-medium text-sm text-green-800 dark:text-green-200">Avvio Manuale</p>
        </div>
        <p className="text-sm text-green-700 dark:text-green-300">
          Questo workflow verrà avviato quando clicchi il pulsante "Esegui" o tramite chiamata API.
          Non richiede configurazione.
        </p>
      </div>
    </div>
  );
}

export function ScheduledTriggerConfig({ config, onChange }: TriggerConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ogni quanto eseguire?</Label>
        <Select
          value={(config.frequency as string) || 'every_day_9am'}
          onValueChange={(v) => onChange('frequency', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="every_minute">Ogni minuto</SelectItem>
            <SelectItem value="every_5_minutes">Ogni 5 minuti</SelectItem>
            <SelectItem value="every_15_minutes">Ogni 15 minuti</SelectItem>
            <SelectItem value="every_30_minutes">Ogni 30 minuti</SelectItem>
            <SelectItem value="every_hour">Ogni ora</SelectItem>
            <SelectItem value="every_day_9am">Ogni giorno alle 9:00</SelectItem>
            <SelectItem value="every_day_18pm">Ogni giorno alle 18:00</SelectItem>
            <SelectItem value="every_monday_9am">Ogni lunedì alle 9:00</SelectItem>
            <SelectItem value="every_month_1st">Primo giorno del mese</SelectItem>
            <SelectItem value="custom">Personalizzato (cron)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {config.frequency === 'custom' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Espressione Cron</Label>
          <Input
            value={(config.cron_expression as string | number) || ''}
            onChange={(e) => onChange('cron_expression', e.target.value)}
            placeholder="0 9 * * *"
            className="font-mono bg-gray-50 dark:bg-gray-900"
          />
          <p className="text-xs text-gray-500">Formato: minuto ora giorno mese giorno_settimana</p>
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fuso Orario</Label>
        <Select
          value={(config.timezone as string) || 'Europe/Rome'}
          onValueChange={(v) => onChange('timezone', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Europe/Rome">Roma (Italia)</SelectItem>
            <SelectItem value="Europe/London">Londra (UK)</SelectItem>
            <SelectItem value="America/New_York">New York (USA)</SelectItem>
            <SelectItem value="UTC">UTC</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function WebhookTriggerConfig({ config, onChange }: TriggerConfigProps) {
  const webhookUrl = config.webhook_name 
    ? `${getPublicBaseUrl()}/webhooks/${config.webhook_name}` 
    : '';
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome Webhook</Label>
        <Input
          value={(config.webhook_name as string | number) || ''}
          onChange={(e) => onChange('webhook_name', e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase())}
          placeholder="es: ordini-shopify"
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Solo lettere minuscole, numeri e trattini</p>
      </div>

      {webhookUrl && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-xs font-medium mb-1 text-blue-700 dark:text-blue-300">Il tuo URL webhook:</p>
          <code className="text-sm font-mono text-blue-600 dark:text-blue-400 break-all">{webhookUrl}</code>
        </div>
      )}
    </div>
  );
}

export function CallEndedTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [trunks, setTrunks] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('trunks').then(setTrunks);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Direzione"
        fieldKey="direction"
        value={(config.direction as string | number) || '*'}
        onChange={(v) => onChange('direction', v)}
        options={[
          { value: '*', label: 'Qualsiasi direzione' },
          { value: 'inbound', label: 'Solo entranti' },
          { value: 'outbound', label: 'Solo uscenti' }
        ]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="call_direction"
        tooltip="Filtra per direzione della chiamata"
      />

      <ConfigFieldWithMode
        label="Esito chiamata"
        fieldKey="disposition"
        value={(config.disposition as string | number) || '*'}
        onChange={(v) => onChange('disposition', v)}
        options={[
          { value: '*', label: 'Qualsiasi esito' },
          { value: 'answered', label: 'Risposta' },
          { value: 'no_answer', label: 'Nessuna risposta' },
          { value: 'busy', label: 'Occupato' },
          { value: 'failed', label: 'Fallita' },
          { value: 'voicemail', label: 'Segreteria' }
        ]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="call_disposition"
        tooltip="Filtra per come si è conclusa la chiamata"
      />

      <ConfigFieldWithMode
        label="Trunk SIP"
        fieldKey="trunk_id"
        value={(config.trunk_id as string | number) || '*'}
        onChange={(v) => onChange('trunk_id', v)}
        options={[{ value: '*', label: 'Qualsiasi trunk' }, ...trunks]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="trunk_id"
        tooltip="Filtra per provider SIP utilizzato"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Durata minima (secondi)</Label>
        <Input
          type="number"
          min={0}
          value={(config.min_duration as string | number) || 0}
          onChange={(e) => onChange('min_duration', parseInt(e.target.value) || 0)}
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Attiva solo per chiamate più lunghe di questo valore (0 = tutte)</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtro chiamante</Label>
        <Input
          value={(config.caller_filter as string | number) || ''}
          onChange={(e) => onChange('caller_filter', e.target.value)}
          placeholder="+39*, 02*"
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Pattern numeri separati da virgola (es: +39* per numeri italiani)</p>
      </div>
    </div>
  );
}

export function CallAnsweredTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [trunks, setTrunks] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('trunks').then(setTrunks);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Direzione"
        fieldKey="direction"
        value={(config.direction as string | number) || '*'}
        onChange={(v) => onChange('direction', v)}
        options={[
          { value: '*', label: 'Qualsiasi direzione' },
          { value: 'inbound', label: 'Solo entranti' },
          { value: 'outbound', label: 'Solo uscenti' }
        ]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="call_direction"
        tooltip="Filtra per direzione della chiamata"
      />

      <ConfigFieldWithMode
        label="Trunk SIP"
        fieldKey="trunk_id"
        value={(config.trunk_id as string | number) || '*'}
        onChange={(v) => onChange('trunk_id', v)}
        options={[{ value: '*', label: 'Qualsiasi trunk' }, ...trunks]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="trunk_id"
        tooltip="Filtra per provider SIP utilizzato"
      />
    </div>
  );
}

export function VoicemailTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [voicemails, setVoicemails] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('voicemails').then(setVoicemails);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Casella vocale"
        fieldKey="voicemail_box_id"
        value={(config.voicemail_box_id as string | number) || '*'}
        onChange={(v) => onChange('voicemail_box_id', v)}
        options={[{ value: '*', label: 'Qualsiasi casella' }, ...voicemails]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="voicemail_box_id"
        tooltip="Attiva solo per messaggi in questa casella"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Durata minima (secondi)</Label>
        <Input
          type="number"
          min={0}
          value={(config.min_duration as string | number) || 0}
          onChange={(e) => onChange('min_duration', parseInt(e.target.value) || 0)}
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Attiva solo per messaggi più lunghi di questo valore</p>
      </div>
    </div>
  );
}

export function SmsTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [phoneNumbers, setPhoneNumbers] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('messaging-senders?channel=sms').then(setPhoneNumbers);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Numero destinatario"
        fieldKey="phone_number_id"
        value={(config.phone_number_id as string | number) || '*'}
        onChange={(v) => onChange('phone_number_id', v)}
        options={[{ value: '*', label: 'Qualsiasi numero' }, ...phoneNumbers]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="phone_number_id"
        tooltip="Attiva solo per SMS ricevuti su questo numero"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contiene parola chiave</Label>
        <Input
          value={(config.keyword_filter as string | number) || ''}
          onChange={(e) => onChange('keyword_filter', e.target.value)}
          placeholder="ORDINE, CONFERMA, STOP"
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Attiva solo se il messaggio contiene una di queste parole (separate da virgola)</p>
      </div>
    </div>
  );
}

export function WhatsappTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [whatsappSenders, setWhatsappSenders] = useState<FieldOption[]>([]);
  const [templates, setTemplates] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('messaging-senders?channel=whatsapp').then(setWhatsappSenders);
    fetchOptions('whatsapp-templates').then(setTemplates);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Numero WhatsApp"
        fieldKey="whatsapp_sender_id"
        value={(config.whatsapp_sender_id as string | number) || '*'}
        onChange={(v) => onChange('whatsapp_sender_id', v)}
        options={[{ value: '*', label: 'Qualsiasi numero' }, ...whatsappSenders]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="whatsapp_sender_id"
        tooltip="Attiva solo per messaggi ricevuti su questo numero"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Contiene parola chiave</Label>
        <Input
          value={(config.keyword_filter as string | number) || ''}
          onChange={(e) => onChange('keyword_filter', e.target.value)}
          placeholder="INFO, PREVENTIVO, CATALOGO"
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Attiva solo se il messaggio contiene una di queste parole</p>
      </div>

      <ConfigFieldWithMode
        label="Risposta a template"
        fieldKey="template_id"
        value={(config.template_id as string | number) || '*'}
        onChange={(v) => onChange('template_id', v)}
        options={[{ value: '*', label: 'Qualsiasi messaggio' }, ...templates]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="template_id"
        tooltip="Attiva solo per risposte a un template specifico"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tipo media</Label>
        <Select
          value={(config.media_type as string) || '*'}
          onValueChange={(v) => onChange('media_type', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="*">Qualsiasi tipo</SelectItem>
            <SelectItem value="text">Solo testo</SelectItem>
            <SelectItem value="image">Immagine</SelectItem>
            <SelectItem value="video">Video</SelectItem>
            <SelectItem value="audio">Audio</SelectItem>
            <SelectItem value="document">Documento</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function CampaignEventTriggerConfig({ config, onChange }: TriggerConfigProps) {
  return (
    <div className="space-y-4">
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-2 mb-2">
          <Megaphone className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          <p className="font-medium text-sm text-purple-800 dark:text-purple-200">Workflow avviato da Campagna</p>
        </div>
        <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
          Questo workflow viene avviato dagli eventi configurati nelle impostazioni della campagna.<br />
          Vai in: <strong>CRM → Campagna → Impostazioni → Workflow Builder</strong> per collegarlo e scegliere l'evento.
        </p>
        <div className="text-xs text-purple-600 dark:text-purple-400 space-y-1">
          <p className="font-medium">Variabili disponibili al runtime:</p>
          <p className="font-mono bg-purple-100 dark:bg-purple-900/40 rounded px-2 py-1">
            contact_id · campaign_id · campaign_name · event_type<br />
            from_status · to_status · hours_in_status · phone · lead_id
          </p>
        </div>
      </div>
    </div>
  );
}

export function LeadCreatedTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fonte</Label>
        <Select
          value={(config.source as string) || '*'}
          onValueChange={(v) => onChange('source', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="*">Qualsiasi fonte</SelectItem>
            <SelectItem value="web_form">Form web</SelectItem>
            <SelectItem value="manual">Inserimento manuale</SelectItem>
            <SelectItem value="import">Importazione</SelectItem>
            <SelectItem value="api">API esterna</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Priorità</Label>
        <Select
          value={(config.priority as string) || '*'}
          onValueChange={(v) => onChange('priority', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="*">Qualsiasi priorità</SelectItem>
            <SelectItem value="high">Alta</SelectItem>
            <SelectItem value="medium">Media</SelectItem>
            <SelectItem value="low">Bassa</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function LeadStageChangedTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [campaigns, setCampaigns] = useState<FieldOption[]>([]);
  const [stages, setStages] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('campaigns').then(setCampaigns);
    fetchOptions('stages').then(setStages);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Campagna"
        fieldKey="campaign_id"
        value={(config.campaign_id as string | number) || '*'}
        onChange={(v) => onChange('campaign_id', v)}
        options={[{ value: '*', label: 'Qualsiasi campagna' }, ...campaigns]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="campaign_id"
        tooltip="Attiva solo per lead di questa campagna"
      />

      <ConfigFieldWithMode
        label="Da stage"
        fieldKey="from_stage_id"
        value={(config.from_stage_id as string | number) || '*'}
        onChange={(v) => onChange('from_stage_id', v)}
        options={[{ value: '*', label: 'Qualsiasi stage' }, ...stages]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="stage_id"
        tooltip="Stage di partenza (prima del cambio)"
      />

      <ConfigFieldWithMode
        label="A stage"
        fieldKey="to_stage_id"
        value={(config.to_stage_id as string | number) || '*'}
        onChange={(v) => onChange('to_stage_id', v)}
        options={[{ value: '*', label: 'Qualsiasi stage' }, ...stages]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="stage_id"
        tooltip="Stage di arrivo (dopo il cambio)"
      />

      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Solo vittorie</Label>
          <p className="text-xs text-gray-500">Attiva solo quando il lead viene vinto</p>
        </div>
        <Switch
          checked={(config.only_won as boolean) || false}
          onCheckedChange={(v) => {
            onChange('only_won', v);
            if (v) onChange('only_lost', false);
          }}
        />
      </div>

      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Solo perdite</Label>
          <p className="text-xs text-gray-500">Attiva solo quando il lead viene perso</p>
        </div>
        <Switch
          checked={(config.only_lost as boolean) || false}
          onCheckedChange={(v) => {
            onChange('only_lost', v);
            if (v) onChange('only_won', false);
          }}
        />
      </div>
    </div>
  );
}

export function CustomerCreatedTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tipo cliente</Label>
        <Select
          value={(config.customer_type as string) || '*'}
          onValueChange={(v) => onChange('customer_type', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="*">Qualsiasi tipo</SelectItem>
            <SelectItem value="B2C">Privato (B2C)</SelectItem>
            <SelectItem value="B2B">Azienda (B2B)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Fonte</Label>
        <Select
          value={(config.source as string) || '*'}
          onValueChange={(v) => onChange('source', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="*">Qualsiasi fonte</SelectItem>
            <SelectItem value="converted_from_lead">Convertito da lead</SelectItem>
            <SelectItem value="manual">Inserimento manuale</SelectItem>
            <SelectItem value="import">Importazione</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {config.customer_type === 'B2B' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Settore (B2B)</Label>
          <Input
            value={(config.sector as string | number) || ''}
            onChange={(e) => onChange('sector', e.target.value)}
            placeholder="IT, Commercio, Manifatturiero"
            className="bg-gray-50 dark:bg-gray-900"
          />
          <p className="text-xs text-gray-500">Filtra per settore aziendale (opzionale)</p>
        </div>
      )}
    </div>
  );
}

export function FormSubmittedTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [forms, setForms] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('forms').then(setForms);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Form"
        fieldKey="form_id"
        value={(config.form_id as string | number) || '*'}
        onChange={(v) => onChange('form_id', v)}
        options={[{ value: '*', label: 'Qualsiasi form' }, ...forms]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="form_id"
        tooltip="Attiva solo per invii da questo form"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">UTM Source</Label>
        <Input
          value={(config.utm_source as string | number) || ''}
          onChange={(e) => onChange('utm_source', e.target.value)}
          placeholder="google, facebook, linkedin"
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Filtra per sorgente campagna (opzionale)</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">UTM Campaign</Label>
        <Input
          value={(config.utm_campaign as string | number) || ''}
          onChange={(e) => onChange('utm_campaign', e.target.value)}
          placeholder="summer2025, blackfriday"
          className="bg-gray-50 dark:bg-gray-900"
        />
        <p className="text-xs text-gray-500">Filtra per nome campagna (opzionale)</p>
      </div>
    </div>
  );
}

export function TagAddedTriggerConfig({ config, onChange, availableVariables = [] }: TriggerConfigProps) {
  const [tags, setTags] = useState<FieldOption[]>([]);
  
  useEffect(() => {
    fetchOptions('tags').then(setTags);
  }, []);

  return (
    <div className="space-y-4">
      <ConfigFieldWithMode
        label="Tag"
        fieldKey="tag_name"
        value={(config.tag_name as string | number) || '*'}
        onChange={(v) => onChange('tag_name', v)}
        options={[{ value: '*', label: 'Qualsiasi tag' }, ...tags]}
        allowAll={false}
        availableVariables={availableVariables}
        fieldContext="tag_name"
        tooltip="Attiva solo quando viene aggiunto questo tag"
      />

      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tipo entità</Label>
        <Select
          value={(config.entity_type as string) || '*'}
          onValueChange={(v) => onChange('entity_type', v)}
        >
          <SelectTrigger className="bg-gray-50 dark:bg-gray-900">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="*">Qualsiasi tipo</SelectItem>
            <SelectItem value="lead">Lead</SelectItem>
            <SelectItem value="customer">Cliente</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export const TRIGGER_CONFIG_COMPONENTS: Record<string, React.FC<TriggerConfigProps>> = {
  trigger_manual: ManualTriggerConfig,
  trigger_scheduled: ScheduledTriggerConfig,
  trigger_webhook: WebhookTriggerConfig,
  trigger_call_hangup: CallEndedTriggerConfig,
  trigger_call_answered: CallAnsweredTriggerConfig,
  trigger_voicemail_left: VoicemailTriggerConfig,
  trigger_sms_received: SmsTriggerConfig,
  trigger_whatsapp_received: WhatsappTriggerConfig,
  trigger_lead_created: LeadCreatedTriggerConfig,
  trigger_lead_stage_changed: LeadStageChangedTriggerConfig,
  trigger_customer_created: CustomerCreatedTriggerConfig,
  trigger_form_submitted: FormSubmittedTriggerConfig,
  trigger_tag_added: TagAddedTriggerConfig,
  trigger_campaign_event: CampaignEventTriggerConfig,
};
